import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';
import useSSELatestSample from '../../hooks/useSSELatestSample';
import GatewayLiveData from './GatewayLiveData';
import GatewayLiveCadence from './GatewayLiveCadence';
import GatewayTelemetry from './GatewayTelemetry';
import { liveVisiblePoints, medianInterval, LIVE_BUFFER_MAX, getTimestamp } from './gatewaySamples';

const fmtKw = value => (value === null || value === undefined ? '—' : `${value} kW`);

const formatTimeOnly = value => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleTimeString();
};

const LiveTile = ({ label, value, sub }) => (
    <div className="gw-live-tile">
        <span className="gw-live-tile__label">{label}</span>
        <span className="gw-live-tile__value">{value}</span>
        {sub ? <span className="gw-live-tile__sub">{sub}</span> : null}
    </div>
);

// Owns everything that depends on the live SSE stream — the sample buffer, the "is live" flag,
// and the 1h seed fetch — so that a streamed sample every interval re-renders only this panel,
// not the whole GatewayPage. It is mounted only on the Overview tab, so the SSE stream is not
// even opened while the Health tab is showing its historical charts. (Previously the stream ran
// unconditionally and every sample re-rendered GatewayHealthHistory, resetting its legend.)
const GatewayLivePanel = ({ deviceId }) => {
    const [dataView, setDataView] = useState('live');
    const [liveSamples, setLiveSamples] = useState([]);
    const [isLive, setIsLive] = useState(false);
    const [recentSamples, setRecentSamples] = useState([]);
    const [recentReady, setRecentReady] = useState(false);

    // One light 1h fetch is reused three ways: the Historical "Last hour" chart, seeding the
    // live graph, and detecting the live sample cadence. Longer historical ranges (24h/7d/30d)
    // fetch on demand in GatewayTelemetry. The live buffer is seeded from the tail; the SSE
    // stream takes over from there.
    useEffect(() => {
        let active = true;
        setRecentSamples([]);
        setRecentReady(false);
        setLiveSamples([]);
        setIsLive(false);

        const to = new Date();
        const from = new Date(to.getTime() - 60 * 60 * 1000);
        api.get_device_samples(deviceId, from.toISOString(), to.toISOString())
            .then(res => {
                if (!active) {
                    return;
                }
                const recent = res?.data ?? [];
                setRecentSamples(recent);
                setRecentReady(true);
                // Seed the live window with enough recent samples to fill the graph at the
                // detected rate, merging with anything the stream already delivered (dedup,
                // sorted, capped to the buffer max).
                const seed = recent.slice(-liveVisiblePoints(medianInterval(recent)));
                setLiveSamples(prev => {
                    // Samples key on `time` (real payload) or `timestamp` (mock/aggregates) — use
                    // getTimestamp so dedup + sort work for both; without this every real sample
                    // collapses under an `undefined` key and the graph gets a single point.
                    const byTime = new Map();
                    [...seed, ...prev].forEach(sample => {
                        const t = getTimestamp(sample)?.getTime();
                        if (t != null) {
                            byTime.set(t, sample);
                        }
                    });
                    return Array.from(byTime.values())
                        .sort((a, b) => getTimestamp(a) - getTimestamp(b))
                        .slice(-LIVE_BUFFER_MAX);
                });
            })
            .catch(() => {
                if (active) {
                    setRecentReady(true);
                }
            });

        return () => {
            active = false;
        };
    }, [deviceId]);

    const onSample = useCallback((eventType, data) => {
        if ((eventType === 'update' || eventType === 'initial_state') && data && data.sensors) {
            setIsLive(true);
            setLiveSamples(prev => [...prev, data].slice(-LIVE_BUFFER_MAX));
        }
    }, []);
    useSSELatestSample(deviceId, onSample);

    const liveLatest = liveSamples.length ? liveSamples[liveSamples.length - 1] : null;
    const liveSensors = liveLatest?.sensors ?? {};

    return (
        <div className="gw-card">
            <div className="gw-readings-head">
                <span className="gw-readings-head__title">Current readings</span>
                {isLive ? (
                    <span className="gw-live__badge">
                        <span className="gw-live__badge-dot" /> Live
                    </span>
                ) : (
                    <span className="gw-live__badge gw-live__badge--idle">Connecting…</span>
                )}
                {liveLatest ? <span className="gw-live__asof">updated {formatTimeOnly(getTimestamp(liveLatest))}</span> : null}
                <GatewayLiveCadence samples={liveSamples} />
            </div>
            <div className="gw-live__tiles">
                <LiveTile label="Consumption" value={fmtKw(liveLatest?.power)} />
                <LiveTile label="Solar" value={fmtKw(liveSensors.power_solar)} />
                <LiveTile
                    label="Battery"
                    value={fmtKw(liveSensors.power_battery)}
                    sub={liveSensors.soc != null ? `SoC ${Math.round(liveSensors.soc * 100)}%` : null}
                />
                <LiveTile label="Facility" value={fmtKw(liveSensors.power_facility)} />
            </div>

            <div className="gw-tabs">
                <button
                    type="button"
                    className={`gw-tabs__btn ${dataView === 'live' ? 'gw-tabs__btn--active' : ''}`}
                    onClick={() => setDataView('live')}
                >
                    <i className="fa-solid fa-bolt" /> Live data
                </button>
                <button
                    type="button"
                    className={`gw-tabs__btn ${dataView === 'historical' ? 'gw-tabs__btn--active' : ''}`}
                    onClick={() => setDataView('historical')}
                >
                    <i className="fa-solid fa-chart-line" /> Historical data
                </button>
            </div>
            {/* Both tabs now have the same shape (controls row + 320px chart), so no jump. */}
            <div className="gw-data-body">
                {dataView === 'live' ? (
                    <GatewayLiveData samples={liveSamples} />
                ) : (
                    <GatewayTelemetry deviceId={deviceId} recentSamples={recentSamples} recentSamplesReady={recentReady} />
                )}
            </div>
        </div>
    );
};

export default GatewayLivePanel;
